import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useNavigationType, useSearchParams } from 'react-router-dom'
import { useTranslation } from '@/i18n'
import { ArrowLeft, Loader2, Search } from 'lucide-react'
import { toast } from './toast'
import AppSidebar from '@/components/AppSidebar'
import DataSidebar from '@/components/DataSidebar'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiTabs,
  IntuiTabsList,
  IntuiTabsTrigger,
} from '@/components/intueri/IntuiTabs'
import { useAppStore } from '@/store'
import {
  apiService,
  type MatchedTagGroup,
  type MatchedTagRow,
  type PendingSourceDocument,
  type PendingTagRow,
} from '@/service/api'
import TagMappingFilterMenu from './TagMappingFilterMenu'
import TagMappingFilterChips from './TagMappingFilterChips'
import {
  boardIsOnlyNarrowing,
  EMPTY_FILTERS,
  narrowedByServerFacet,
  type BoardOption,
  type TagMappingFilters,
} from './filters'
import EditMappingDialog, { type EditMappingTarget } from './EditMappingDialog'
import SourceFilePreviewOverlay from './SourceFilePreviewOverlay'
import { isAnnotatorRole } from '@/types/users'
import { notifyAnnotatorBlocked } from '@/service/annotatorAccess'
import MatchedTable from './MatchedTable'
import PendingTable from './PendingTable'
import { DEFAULT_SORT, nextSort } from './sort'
import { readView, writeView, type TabKey } from './urlState'
import HelpAgentLauncherSlot from '@/components/HelpAgent/HelpAgentLauncherSlot'
import { PAGE_HEADER, PAGE_TITLE } from '@/components/layout/pageHeader'

/**
 * Rows fetched per batch as the reader scrolls.
 *
 * The list GROWS: each batch is appended to what is already on screen, so
 * scrolling never takes rows away and the reader keeps their place. Fifty is
 * the batch Pratima asked for — "may be first 50 entries are visible then when
 * i scroll more next 50 will load but previous entries shouldnt gone" — and the
 * sentinel fetches the next fifty before the reader reaches the bottom.
 */
const PAGE_SIZE = 50
/**
 * The most rows one `/matched` or `/pending` request may ask for — both routes
 * declare `Query(..., le=200)` and FastAPI answers 422 above it. A refresh of
 * everything the reader has scrolled through is therefore issued as bounded
 * chunks and stitched back together, never as one request for the whole range
 * (Abdullah, #34392: after the fifth batch a single `limit=250` was rejected,
 * so a successful delete produced a list-load error instead of a refreshed list).
 */
const API_PAGE_LIMIT = 200

/**
 * A tab's count pill. `null` means the number is not known yet — it renders as
 * an em-dash rather than 0, because "we have not counted" and "there is
 * nothing" are different facts and the page had no way to say the first.
 */
/**
 * The Pending badge's last known value, per tenant, in `localStorage`.
 *
 * Pending's only source while you sit on Matched is `/summary` (1.1s avg, 2.9s
 * worst), since its list was never fetched, so the badge shows a dash until
 * that lands. Remembering the last number gives it an instant local value.
 *
 * TENANT-SCOPED KEY, deliberately. One shared key would show the previous
 * tenant's queue depth after a switch — a wrong number presented as this
 * tenant's, which is worse than the dash it replaces.
 *
 * Both directions swallow. Storage throws in a private window and where site
 * data is blocked, and a remembered badge value is never worth breaking the page.
 */
const pendingSeedKey = (orgId: string): string => `tagmapping:pending:${orgId}`

const readPendingSeed = (orgId: string | null): number | null => {
  if (!orgId) return null
  try {
    const raw = window.localStorage.getItem(pendingSeedKey(orgId))
    if (raw === null) return null
    const n = Number.parseInt(raw, 10)
    // A stored value is only a hint; anything unparseable or negative is ignored
    // rather than rendered, so a corrupted entry cannot put "NaN" in the badge.
    return Number.isFinite(n) && n >= 0 ? n : null
  } catch {
    return null
  }
}

const writePendingSeed = (orgId: string | null, value: number | null): void => {
  if (!orgId || value === null || value < 0) return
  try {
    window.localStorage.setItem(pendingSeedKey(orgId), String(value))
  } catch {
    /* storage unavailable — the badge just falls back to the server */
  }
}

const TabCount = ({
  value,
  approximate = false,
}: {
  value: number | null
  approximate?: boolean
}) => (
  <span
    className="rounded-full bg-foreground px-1.5 py-0.5 text-[11px] font-medium text-background"
    aria-label={
      value === null
        ? 'count unavailable'
        : approximate
          ? `approximately ${value}`
          : undefined
    }
    // The tilde is the whole point of the flag: the badge is always a number now,
    // and an estimate that looked exact would be the confident-wrong-number the
    // dash was introduced to avoid.
    title={approximate ? 'approximate until the tab loads the exact count' : undefined}
  >
    {value === null ? '–' : `${approximate ? '~' : ''}${value}`}
  </span>
)

/**
 * Document types the tenant matcher partitions into a P&ID or timeseries side.
 * Anything else contributes no tags, so sending it just makes the run slower.
 */
const MATCHABLE_DOC_TYPES = ['pnid', 'pfd', 'graph', 'timeseries']

/** Source types whose viewer is a sheet — the routes an annotator is turned away from. */
const isSheetSourceType = (type?: string | null): boolean =>
  ['timeseries', 'datasheet', 'excel'].includes((type ?? '').toLowerCase())

/**
 * Tag mapping (FS-655) — the standing review surface for entity matching.
 *
 * Reads the tenant-scoped matches the Data Catalog "Match Entity" run
 * produces: Matched shows what was paired and how confidently, Pending shows
 * the tags it could not place, each connectable by hand.
 */
const TagMappingPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()

  // The URL is authoritative, including when Back/Forward changes it.
  const view = useMemo(() => readView(searchParams), [searchParams])
  const { tab, search, filters, sorts } = view
  const updateView = useCallback(
    (patch: Partial<typeof view>, replace = false) =>
      setSearchParams(writeView(searchParams, { ...view, ...patch }), { replace }),
    [searchParams, setSearchParams, view],
  )
  const setTab = (next: TabKey) => updateView({ tab: next })
  const setSearch = (next: string) => updateView({ search: next }, true)
  const navigationType = useNavigationType()
  const [delayedSearch, setDebouncedSearch] = useState(search)
  // A restored URL must use its search in the same render as its tab/filters.
  // Only typing waits for the debounce; POP covers Back/Forward and initial loads.
  const debouncedSearch = navigationType === 'POP' ? search : delayedSearch

  const [matchedGroups, setMatchedGroups] = useState<MatchedTagGroup[]>([])
  const [matchedTotal, setMatchedTotal] = useState<number | null>(null)
  const [pendingRows, setPendingRows] = useState<PendingTagRow[]>([])
  const [pendingTotal, setPendingTotal] = useState<number | null>(null)
  /** Rows still awaiting a decision — what the Pending badge should show. */
  const [undecidedTotal, setUndecidedTotal] = useState<number | null>(null)
  /**
   * Server counts. `null` on a side means UNKNOWN, not zero — pending is no
   * longer computed by the summary endpoint because it took up to 279s, and a
   * count nobody waited for must not render as 0.
   */
  const activeOrganizationId = useAppStore((st) => st.activeOrganizationId)
  /**
   * Keyed on the TENANT ID, not read once at mount.
   *
   * The first version used a `useState` initializer, which runs on the first
   * render — and `activeOrganizationId` is populated by the ASYNC auth flow
   * (`authSessionSlice` sets it after fetching userInfo). So on a fresh page load
   * it read `null`, returned `null`, and stayed null for the whole session even
   * once the id arrived: the seed never fired in exactly the case it exists for.
   *
   * A memo keyed on the id re-reads the moment it lands. Re-reading later cannot
   * resurrect a stale value over a better one, because the seed is the LAST rung
   * of the badge ladder — any server number outranks it.
   */
  const pendingSeed = useMemo(
    () => readPendingSeed(activeOrganizationId),
    [activeOrganizationId],
  )
  const [counts, setCounts] = useState<{
    /** The board the counts were taken for; '' for the whole tenant. */
    boardId: string
    matched: number | null
    pending: number | null
    pendingExact: boolean
  } | null>(null)
  const [loading, setLoading] = useState(true)
  /**
   * A load that will REPLACE the rows on screen (page 0 after a tab, search,
   * filter or sort change; the range refresh after a row action), as opposed to
   * one appending a page below them. The tables keep the current rows visible
   * and dimmed while this is true, rather than showing nothing (review F10 /
   * G3: "preserve stable content while reloading and visibly mark it as
   * updating"). The dimming is the visible mark; the "Updating" status is
   * screen-reader only, since a visible line jittered the table on every sort.
   */
  const [reloading, setReloading] = useState(false)
  /**
   * The row whose mapping is being re-pointed, or null. One dialog for the whole
   * table rather than one per row -- the picker fetches candidates on open, and
   * a per-row instance would mount hundreds of them.
   */
  const [editTarget, setEditTarget] = useState<EditMappingTarget | null>(null)
  /**
   * The file a Source file cell opened, in the SAME preview the Intelligent
   * Board opens a card with, minus Remove from Board (`SourceFilePreviewOverlay`).
   */
  const [sourcePreview, setSourcePreview] = useState<{
    id: string
    name: string
    /** The tag to land on, as this file spells it. */
    highlightTag?: string
  } | null>(null)
  const [running, setRunning] = useState(false)
  const sort = sorts[tab] ?? DEFAULT_SORT
  const handleSort = (key: string) =>
    updateView({ sorts: { ...sorts, [tab]: nextSort(sort, key) } })

  /**
   * Where the header's back control goes (UI-32). Board-scoped arrivals return
   * to that board; everything else came through Data, whose catalogue is this
   * page's section home.
   */
  const backTo = filters.boardId
    ? {
        path: `/board/${filters.boardId}`,
        labelKey: 'data_catalog:tag_mapping.back_to_board',
        fallback: 'Back to board',
      }
    : {
        path: '/data-catalog',
        labelKey: 'data_catalog:tag_mapping.back_to_data_catalog',
        fallback: 'Back to Data Catalog',
      }

  /** Boards to offer in the Filter menu. Fetched when the menu first opens. */
  const [boards, setBoards] = useState<BoardOption[]>([])
  /** The scoped board's name, fetched on its own — see `boardName` below. */
  const [boardName, setBoardName] = useState('')
  const [pendingDocuments, setPendingDocuments] = useState<PendingSourceDocument[]>([])
  /**
   * How many hits the OTHER tab has for the current search. One search box
   * spans two tabs, so a tag that exists only on the other one otherwise looks
   * like "search is broken" — which is exactly how it read in review.
   */
  const [otherTabHits, setOtherTabHits] = useState<number | null>(null)
  /**
   * The ACTIVE tab's own request failed.
   *
   * Tracked separately from the counts because the two fail independently: a
   * summary that succeeded kept the counts-unavailable state false, so a
   * failed Pending request fell through to the table's own "nothing to
   * review" empty state — the same lie this change exists to remove, one
   * layer down.
   */
  const [listFailed, setListFailed] = useState(false)

  const pageRef = useRef(0)
  /** Monotonic id so a slow earlier response cannot clobber a newer one. */
  const requestRef = useRef(0)
  /** The same, for the counts — they race independently of the rows. */
  const countsRef = useRef(0)

  useEffect(() => {
    if (navigationType === 'POP') {
      setDebouncedSearch(search)
      return
    }
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search, navigationType])

  const loadCounts = useCallback(async () => {
    // Generation-guarded, like loadPage. A connect adjusts the counts locally
    // and then refetches, so a summary already in flight can land afterwards
    // carrying the PRE-connect numbers and reinstate them — the badge snapping
    // back to the total you just moved. Harmless while pending was always null;
    // this change gives it a real number, which is what makes it visible.
    const requestId = ++countsRef.current
    try {
      const summary = await apiService.getTagMappingSummary({ boardId: filters.boardId || undefined })
      if (requestId !== countsRef.current) return
      setCounts({
        boardId: filters.boardId || '',
        matched: summary.matched_count ?? null,
        pending: summary.pending_count ?? null,
        pendingExact: summary.pending_count_exact !== false,
      })
    } catch {
      if (requestId !== countsRef.current) return
      // A failed count must not blank the page that is otherwise loading fine:
      // Pending falls back to its page totals, Matched shows a dash.
      setCounts(null)
    }
  }, [filters.boardId])

  /**
   * One request for `limit` rows from `offset`, on the current tab with the
   * current SERVER facets. Shared by the page loader and the range refresh so
   * the two can never disagree about what a page is.
   */
  const fetchRange = useCallback(
    (offset: number, limit: number) =>
      tab === 'matched'
        ? apiService.getMatchedTags({
            limit,
            offset,
            search: debouncedSearch || undefined,
            method: filters.method || undefined,
            confidence: filters.confidence || undefined,
            boardId: filters.boardId || undefined,
            sortBy: sort?.key,
            sortDirection: sort?.direction,
          })
        : apiService.getPendingTags({
            limit,
            offset,
            search: debouncedSearch || undefined,
            documentId: filters.documentId || undefined,
            source: filters.source || undefined,
            boardId: filters.boardId || undefined,
            sortBy: sort?.key,
            sortDirection: sort?.direction,
          }),
    // The SERVER facets only. `filters` as a whole would put `sourceType` in
    // here too -- a render-only facet that is never sent -- and the reset
    // effect below would then refetch page zero and discard everything
    // already paginated each time it changed: a loading flash, needless
    // traffic, and the reader's scroll position gone, for a filter the client
    // applies to rows it already has.
    [tab, debouncedSearch, filters.method, filters.confidence, filters.documentId, filters.source, filters.boardId, sort?.key, sort?.direction],
  )

  /** Put one or more responses for the current tab into state, in order. */
  const applyResponses = useCallback(
    (responses: Awaited<ReturnType<typeof fetchRange>>[], append: boolean) => {
      if (tab === 'matched') {
        const pages = responses as Awaited<ReturnType<typeof apiService.getMatchedTags>>[]
        const groups = pages.flatMap((r) => r.groups)
        setMatchedGroups((prev) => (append ? [...prev, ...groups] : groups))
        setMatchedTotal(pages[pages.length - 1]?.total ?? null)
      } else {
        const pages = responses as Awaited<ReturnType<typeof apiService.getPendingTags>>[]
        const rows = pages.flatMap((r) => r.rows)
        const last = pages[pages.length - 1]
        setPendingRows((prev) => (append ? [...prev, ...rows] : rows))
        setPendingTotal(last?.total ?? null)
        setUndecidedTotal(last?.undecided_total ?? last?.total ?? null)
        // Faceted over the whole set, so it survives selecting a sheet.
        if (last?.documents) setPendingDocuments(last.documents)
      }
      setListFailed(false)
    },
    [tab],
  )

  /** The shared failure path: the total is now UNKNOWN, not zero. */
  const failLoad = useCallback(
    (err: unknown) => {
      // Leaving it at 0 is what made a failed load render as "nothing to review".
      if (tab === 'matched') setMatchedTotal(null)
      else {
        setPendingTotal(null)
        setUndecidedTotal(null)
      }
      setListFailed(true)
      toast.error(
        err instanceof Error
          ? err.message
          : t('data_catalog:tag_mapping.load_failed', {
              defaultValue: 'Could not load tag mappings.',
            }),
      )
    },
    [tab, t],
  )

  const loadPage = useCallback(
    async (page: number, append: boolean) => {
      // Tab / search / filter changes fire overlapping requests. Without this
      // an older response finishing last replaces the newer rows, and its
      // `finally` clears the shared loading flag so pagination advances while
      // the current request is still in flight.
      const requestId = ++requestRef.current
      setLoading(true)
      if (!append) setReloading(true)
      try {
        const resp = await fetchRange(page * PAGE_SIZE, PAGE_SIZE)
        if (requestId !== requestRef.current) return
        applyResponses([resp], append)
      } catch (err) {
        if (requestId !== requestRef.current) return
        failLoad(err)
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false)
          setReloading(false)
        }
      }
    },
    [fetchRange, applyResponses, failLoad],
  )

  /**
   * Re-read the first `rows` rows — everything the reader has scrolled
   * through — and replace the list with the answer, in requests the API
   * accepts: chunks of at most API_PAGE_LIMIT, in parallel, stitched in order.
   */
  const reloadLoadedRange = useCallback(
    async (rows: number) => {
      const requestId = ++requestRef.current
      setLoading(true)
      setReloading(true)
      try {
        const chunks: { offset: number; limit: number }[] = []
        for (let offset = 0; offset < rows; offset += API_PAGE_LIMIT) {
          chunks.push({ offset, limit: Math.min(API_PAGE_LIMIT, rows - offset) })
        }
        const responses = await Promise.all(chunks.map((c) => fetchRange(c.offset, c.limit)))
        if (requestId !== requestRef.current) return
        applyResponses(responses, false)
      } catch (err) {
        if (requestId !== requestRef.current) return
        failLoad(err)
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false)
          setReloading(false)
        }
      }
    },
    [fetchRange, applyResponses, failLoad],
  )

  // Reset to page 0 whenever the tab or the search changes.
  useEffect(() => {
    pageRef.current = 0
    void loadPage(0, false)
  }, [loadPage])

  // Nothing here for this search? Check the other tab so we can point at it.
  useEffect(() => {
    const current = tab === 'matched' ? matchedTotal : pendingTotal
    // `null` means this tab's total never loaded, so "nothing here" is not
    // established and pointing at the other tab would be a guess.
    if (loading || !debouncedSearch || current === null || current > 0) {
      setOtherTabHits(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        // Scoped to the same board, or the hint offers to send you to a tab
        // that then shows nothing.
        const probe = {
          limit: 1,
          offset: 0,
          search: debouncedSearch,
          boardId: filters.boardId || undefined,
        }
        const resp =
          tab === 'matched'
            ? await apiService.getPendingTags(probe)
            : await apiService.getMatchedTags(probe)
        if (!cancelled) setOtherTabHits(resp.total)
      } catch {
        if (!cancelled) setOtherTabHits(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tab, debouncedSearch, loading, matchedTotal, pendingTotal, filters.boardId])

  useEffect(() => {
    void loadCounts()
  }, [loadCounts])

  /**
   * The Filter menu's board list, fetched the first time the menu opens.
   *
   * NOT on mount: `GET /v1/workspaces` is unpaginated and returns every board
   * the user can see — 13,472 on staging and 109,888 on dev, per the
   * measurements in `workspaces.service.ts` — so a reader who never opens the
   * facet must not pay for it. The chip does not need this list either; it
   * fetches its one board below.
   *
   * Failing is not worth a toast: the page works, the menu is just short a
   * facet.
   */
  const boardsRequested = useRef(false)
  const loadBoards = useCallback(async () => {
    if (boardsRequested.current) return
    boardsRequested.current = true
    try {
      const { items: workspaces } = await apiService.listWorkspaces()
      setBoards(workspaces.map((workspace) => ({ id: workspace.id, name: workspace.name })))
    } catch {
      // Allow a retry on the next open rather than failing closed forever.
      boardsRequested.current = false
      setBoards([])
    }
  }, [])

  /**
   * The scoped board's NAME, for the chip — one board, not the whole list.
   *
   * Arriving from a board means the chip has to name it immediately, and
   * scanning a 109,888-entry list for one id would make the chip's label
   * depend on a request it has no other reason to make. A single
   * `getWorkspace` is cheap and independent of how many boards exist.
   */
  useEffect(() => {
    if (!filters.boardId) {
      setBoardName('')
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const workspace = await apiService.getWorkspace(filters.boardId)
        if (!cancelled) setBoardName(workspace.name)
      } catch {
        // The chip falls back to the generic label; the filter still works.
        if (!cancelled) setBoardName('')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [filters.boardId])

  /**
   * FS-675 — record a verdict on a pending row, and MARK IT IN PLACE.
   *
   * Nothing is removed. A reviewer who decides a tag and watches the row vanish
   * gets no confirmation it registered, no sense of what they have got through,
   * and no way back to a mistake — so the row keeps its place and shows its
   * verdict, exactly as the server now returns it on a fresh load.
   *
   * Marked by DISPLAY as well as by row, because a merged OCR region puts several
   * tags in one string: `AC-036950` and
   * `'AD-036204 AB-036227 AB-036305 AC-036950'` are two rows with one display, and
   * the server marks both. A client that marked only the row clicked left a
   * visually identical twin looking undecided.
   *
   * A CORRECTION refetches instead. It can land on a different identity than the
   * row it came from (fixing punctuation keeps it, fixing a misread tag does not),
   * and only the server knows which — the identity rule lives in Python, and a
   * second copy here would be free to drift from the one that keyed the verdict.
   */

  const loadMore = useCallback(() => {
    if (loading) return
    pageRef.current += 1
    void loadPage(pageRef.current, true)
  }, [loading, loadPage])

  /** Both halves failed together often enough that one control retries both. */
  const handleRetryLoad = useCallback(() => {
    pageRef.current = 0
    void loadCounts()
    void loadPage(0, false)
  }, [loadCounts, loadPage])

  /**
   * A tab's count, or null when genuinely unknown.
   *
   * `matchedTotal` / `pendingTotal` are only meaningful for the tab that has
   * actually loaded — the other one sits at its initial 0. Falling back to
   * them unconditionally is what rendered "Pending 0" for a tenant with 5,077
   * pending tags while the summary request was still in flight.
   */
  //
  // With a BOARD filter on, the tenant-wide Pending summary is the wrong number
  // entirely — the badge would read 226 next to a list showing the 12 tags on
  // this board — so Pending reads the tab's own total while the filter is up.
  const boardScoped = !!filters.boardId
  // Matched always reads the summary, which counts LINES (a tag mapped five
  // times is five) and is asked for the board when one is set. The page total
  // is no stand-in: unsorted it counts TAGS, so falling back to it showed 55
  // and then jumped to 123. Counts taken for another scope are not shown
  // either: switching the board must not flash the previous board's number.
  const matchedCount =
    counts && counts.boardId === (filters.boardId || '') ? counts.matched : null
  // What the PAGE STATES below read: is anything mapped, and is it known at
  // all. The page total answers both while the summary is in flight or has
  // failed — a tag is listed only if it has a live mapping, so tags and lines
  // are zero together. Reading the badge's count here instead put "Couldn't
  // load your tag mappings" over rows that had loaded (Greptile on #37048).
  const matchedKnown = matchedCount ?? matchedTotal
  /**
   * The Pending badge: the number AND whether it is exact, decided together.
   *
   * Together, because deriving them separately is how an estimate ends up
   * rendered as a fact — the first version preferred `counts.pending`
   * unconditionally while flagging approximate only when the tab had not loaded,
   * so once Pending loaded, the ESTIMATE was shown with no tilde beside an exact
   * number it had beaten.
   *
   * Order of preference:
   *  1. an EXACT tenant-wide count from the summary (what the badge is for, and
   *     what the connect-race guard depends on winning);
   *  2. the tab's own totals, which are exact but describe the FILTERED set —
   *     right when a board scope is on, and the best available otherwise;
   *  3. the SQL estimate, last, always marked, and only when nothing exact
   *     contradicts it (below).
   */
  /**
   * Whether `search`, the sheet facet or the source facet narrows the Pending
   * list. The tab's totals then describe the hits, not the tenant's queue.
   */
  const pendingNarrowed = !!debouncedSearch || !!filters.documentId || !!filters.source
  const { value: pendingCount, approximate: pendingApproximate } = useMemo(() => {
    const filtered = undecidedTotal ?? pendingTotal
    if (boardScoped) return { value: filtered, approximate: false }
    if (counts?.pending != null && counts.pendingExact) {
      return { value: counts.pending, approximate: false }
    }
    // The tab's totals describe the FILTERED set — `search` and the facets are
    // applied server-side before `total`/`undecided_total` are computed — so
    // with a search active they are the hit count, not the tenant's pending work.
    // Using them for the tenant-wide badge would present "3 matches" as "3
    // pending". Only trusted outright when nothing is narrowing the set.
    if (filtered != null && !pendingNarrowed) return { value: filtered, approximate: false }
    // THE ESTIMATE, guarded by what is already known exactly (review D4; QA
    // 2026-09-29 saw "~0" over a list of rows):
    //  - an approximate ZERO is unknown, not zero. The estimate reads planner
    //    statistics, and a table without them estimates 0 — the one value that
    //    cannot be told apart from "no statistics". Exact zero still renders.
    //  - a narrowed list's exact total is a LOWER BOUND on the tenant's queue, so
    //    an estimate below it is wrong; the bound itself is shown instead, still
    //    marked approximate because the whole is at least that.
    const estimate = counts?.pending != null && counts.pending > 0 ? counts.pending : null
    if (filtered != null && (estimate == null || estimate < filtered)) {
      return { value: filtered, approximate: true }
    }
    if (estimate != null) return { value: estimate, approximate: true }
    // LAST RUNG BEFORE THE DASH: the number this tenant's badge showed last
    // time. Marked approximate because it predates this page load and any
    // decision made since, and it is replaced the moment `/summary` answers.
    // This is what removes the dash on every load after the first.
    if (pendingSeed != null) return { value: pendingSeed, approximate: true }
    return { value: null, approximate: false }
  }, [boardScoped, undecidedTotal, pendingTotal, counts, pendingNarrowed, pendingSeed])

  /**
   * Remember the badge value for the next load — but ONLY when it is exact and
   * tenant-wide. Storing an approximate or board/search-scoped figure would seed
   * the next session with a number that was never the queue depth, and the seed
   * is shown before anything can correct it.
   */
  useEffect(() => {
    // Any tenant-wide number is worth remembering, exact or not: the seed's only
    // job is the FIRST paint, and it renders with a tilde either way (it is the
    // approximate rung). Requiring exactness meant a reviewer who never opened
    // the Pending tab stored nothing and kept getting the em-dash.
    //
    // `boardScoped` still excluded — that number describes one board, not the
    // tenant, and seeding the badge with it would misreport the queue depth.
    // Nor a NARROWED figure: a lower bound from a search is not the tenant's
    // queue, and seeding the next load with it would understate the work.
    if (pendingCount == null || boardScoped || pendingNarrowed) return
    writePendingSeed(activeOrganizationId, pendingCount)
  }, [pendingCount, boardScoped, pendingNarrowed, activeOrganizationId])

  const hasMore = useMemo(
    () =>
      tab === 'matched'
        ? matchedTotal !== null && matchedGroups.length < matchedTotal
        : pendingTotal !== null && pendingRows.length < pendingTotal,
    [tab, matchedGroups.length, matchedTotal, pendingRows.length, pendingTotal],
  )

  /**
   * "There is nothing to review here yet, and running matching is what fixes
   * it" — a POSITIVE claim, so every count it rests on must be KNOWN. It
   * previously fell back to page totals that had never loaded, which asserted
   * an empty tenant on dev's 165 matched / 5,077 pending. A failed load is
   * reported as a failure instead.
   *
   * It rests on the matched count plus the ACTIVE tab's, not on both tabs:
   * pending is no longer carried by the summary (it cost 167s), so requiring
   * it too meant a genuinely empty tenant never saw the call to action until
   * it visited the Pending tab. On Matched the two collapse to one condition;
   * on Pending both must be known-zero before the tab's rows are replaced.
   */
  const activeCount = tab === 'matched' ? matchedKnown : pendingCount
  //
  // `!boardScoped` is a SAFETY condition, not just a cosmetic one, and that is
  // not obvious from the name: this state's action is `handleRunMatching`,
  // which runs matching over EVERY document in the tenant (`listDocsPaginated`,
  // 500 at a time) and takes minutes. Offering it because one board happens to
  // hold no tags would be a non-sequitur with a real cost. `noRowsOnBoard`
  // below is the board-scoped case, and its action is "drop the filter".
  const isEmptyTenant =
    !loading && !debouncedSearch && !boardScoped && matchedKnown === 0 && activeCount === 0

  /**
   * Scoped to a board that has no tags on this tab.
   *
   * Distinguished from `isEmptyTenant` because the CTA is the opposite: the
   * tenant has plenty of matches, they are just not on THIS board, so offering
   * "Run matching over your documents" would be a non-sequitur that starts a
   * multi-minute job. What the reader wants is the filter off.
   */
  //
  // Only when the board is the SOLE narrowing. With Board + Confidence → Weak
  // the board may well have tags; the page is empty because of the second
  // facet, and "Show all tags" (board off) would leave it empty. That case
  // falls through to the table's own filtered empty state, whose Clear filters
  // drops every facet (Greptile on #35073).
  const noRowsOnBoard =
    !loading &&
    boardScoped &&
    !debouncedSearch &&
    activeCount === 0 &&
    boardIsOnlyNarrowing(filters, tab)

  /**
   * The counts could not be established, or the tab's own rows could not be
   * fetched — say so rather than implying zero.
   *
   * The list half matters even when the summary succeeded: a known matched
   * count kept this false while the Pending request had failed, and the empty
   * table underneath then read as "nothing to review". Rows already on screen
   * are kept (a failed *append* is reported by its toast); this is for the
   * case where there is nothing to show and no reason given.
   */
  const visibleRows = tab === 'matched' ? matchedGroups.length : pendingRows.length
  const loadUnavailable =
    !loading &&
    ((matchedKnown === null && pendingCount === null) || (listFailed && visibleRows === 0))

  const handleFiltersChange = useCallback(
    (next: TagMappingFilters) => updateView({ filters: next }),
    [updateView],
  )

  /** What the board chip's ✕ and the board-empty state both do. */
  const clearBoardFilter = useCallback(
    () => handleFiltersChange({ ...filters, boardId: '' }),
    [filters, handleFiltersChange],
  )

  /** What the tables' filtered empty state offers: every facet off at once. */
  const clearAllFilters = useCallback(
    () => handleFiltersChange(EMPTY_FILTERS),
    [handleFiltersChange],
  )
  /**
   * A server facet is narrowing this tab, so an empty page means "nothing
   * matches these filters", not "nothing mapped yet" (review D1: Source type →
   * Document + Confidence → Weak read "No matched tags yet." while the tab
   * still counted 273).
   */
  const narrowed = narrowedByServerFacet(filters, tab)

  const otherTab: TabKey = tab === 'matched' ? 'pending' : 'matched'
  const tabLabel = (key: TabKey) =>
    key === 'matched'
      // Follows the tab's own label: this hint names the tabs in prose, so
      // "no Matched tags" beside a tab reading "Mapped" would name nothing.
      ? t('data_catalog:tag_mapping.cross_tab.matched', { defaultValue: 'Mapped' })
      : t('data_catalog:tag_mapping.cross_tab.pending', { defaultValue: 'Pending' })
  const thisTabLabel = tabLabel(tab)
  const otherTabLabel = tabLabel(otherTab)
  const showCrossTabHint =
    !loading &&
    !!debouncedSearch &&
    (tab === 'matched' ? matchedTotal : pendingTotal) === 0 &&
    (otherTabHits ?? 0) > 0

  /**
   * A Source file cell, on either tab. A sheet's viewer sits behind
   * `AnnotatorRestrictedRoute` on its own route; the overlay has no route to
   * guard, so the guard is here: an annotator gets the same notice the route
   * gives, and no viewer (Greptile, #34218).
   */
  const userRole = useAppStore((st) => st.user?.role)
  const handleSelectSourceFile = useCallback(
    (documentId: string, documentName: string, sourceType?: string | null, highlightTag?: string) => {
      if (isAnnotatorRole(userRole) && isSheetSourceType(sourceType)) {
        notifyAnnotatorBlocked()
        return
      }
      setSourcePreview({ id: documentId, name: documentName, highlightTag })
    },
    [userRole],
  )

  // A pending row knows which source the tag was read from, so it passes that
  // on rather than leaving the drill-down to guess from what it finds.
  const handleSelectTag = useCallback(
    (tag: string, source?: 'sheet' | 'document') => {
      const path = `/tag-mapping/${encodeURIComponent(tag)}`
      const detailParams = new URLSearchParams()
      if (source) detailParams.set('source', source)
      if (searchParams.size) detailParams.set('returnTo', `/tag-mapping?${searchParams}`)
      navigate(detailParams.size ? `${path}?${detailParams}` : path)
    },
    [navigate, searchParams],
  )

  /**
   * Auto-matching now runs on upload, but it only sees documents ingested
   * SINCE it was turned on — a tenant whose uploads predate that, and which
   * never used the Data Catalog's Match Entity action, has nothing to review.
   * Run it here over every P&ID and time series document so the page can fill
   * itself rather than sending the user somewhere else to do it.
   */
  const handleRunMatching = useCallback(async () => {
    setRunning(true)
    try {
      const docs = await apiService.listDocsPaginated({ page_size: 500, page_number: 0 })
      const documentIds = (docs.documents || [])
        .filter((doc) => MATCHABLE_DOC_TYPES.includes(String(doc.type)))
        .map((doc) => doc.id)
      if (documentIds.length === 0) {
        toast.error(
          t('data_catalog:tag_mapping.run.no_documents', {
            defaultValue: 'Upload a P&ID and a time series file first.',
          }),
        )
        return
      }
      const resp = await apiService.runTenantEntityMatching({ document_ids: documentIds })
      toast.success(
        t('data_catalog:tag_mapping.run.success', {
          defaultValue: 'Matched {{count}} tag pair(s).',
          count: resp.match_count,
        }),
      )
      pageRef.current = 0
      await Promise.all([loadPage(0, false), loadCounts()])
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('data_catalog:tag_mapping.run.failed', {
              defaultValue: 'Could not run matching.',
            }),
      )
    } finally {
      setRunning(false)
    }
  }, [loadPage, loadCounts, t])

  /**
   * A connected tag has left Pending for Matched. Drop it locally and move the
   * counts so the tabs are right immediately, then reconcile with the server.
   */

  /**
   * Re-read page 0 and the badges after a row action.
   *
   * Every one of them can move a row BETWEEN the two tabs — an approve or an
   * edit sends it to Matched, a delete sends it back to Pending — so patching
   * the row in place would leave the page asserting something the server no
   * longer agrees with, and both tab counts stale.
   */
  const reloadAfterRowAction = useCallback(() => {
    // Re-read EVERY page the reader has scrolled through and replace the list
    // with the answer. It used to go back to page 0, which threw away the
    // pages below it and put the reviewer back at the top after each action
    // -- Pratima: "previous entries shouldnt gone". The page counter stays
    // where it is, so the next scroll appends after the last row the reader
    // already had. Issued in API-sized chunks, never one oversized request.
    void reloadLoadedRange((pageRef.current + 1) * PAGE_SIZE)
    void loadCounts()
  }, [reloadLoadedRange, loadCounts])

  /** Shared failure path — the action's own message, or the server's. */
  const reportActionError = useCallback(
    (err: unknown, fallback: string) => {
      toast.error(err instanceof Error ? err.message : fallback)
    },
    [],
  )

  /**
   * Pending → Approve: accept the candidate the row already proposes.
   *
   * This is `connect`, not a new endpoint: the row's "Mapped tag" cell IS the
   * proposal, and approving it is the same write the picker performs once you
   * have chosen the same target by hand. `candidate_tag` is therefore required
   * — the menu disables the item without one, and this re-checks rather than
   * trusting the caller.
   */
  const handleApprovePending = useCallback(
    async (row: PendingTagRow) => {
      if (!row.candidate_tag) return
      try {
        await apiService.connectTag({
          pid_tag: row.tag,
          ts_tag: row.candidate_tag,
          // Only a DRAWING's id may travel as `pid_document_id`. On a prose
          // row `source_document_id` is a document, and `connect` stores
          // whatever it is given -- the match would claim provenance on a
          // P&ID the tag was never on. Undefined rather than wrong.
          pid_document_id:
            row.source === 'sheet' ? (row.source_document_id ?? undefined) : undefined,
        })
        toast.success(
          t('data_catalog:tag_mapping.row_actions.approved', {
            defaultValue: 'Approved mapping successfully',
          }),
        )
        reloadAfterRowAction()
      } catch (err) {
        reportActionError(
          err,
          t('data_catalog:tag_mapping.row_actions.approve_failed', {
            defaultValue: 'Could not approve the mapping.',
          }),
        )
      }
    },
    [t, reloadAfterRowAction, reportActionError],
  )

  /**
   * Matched → Delete: REJECT the match.
   *
   * There is no endpoint that erases a match row, and inventing one here would
   * mean inventing its semantics too. Rejection is the existing primitive with
   * the effect the frame draws: the tenant feedback row flips the pair to
   * rejected, the registry's confirmed leg drops it, and the tag returns to
   * Pending. It is also the only reversible spelling of "delete" — a later
   * approve puts the mapping back, which an erase could not.
   */
  const handleDeleteMatch = useCallback(
    async (match: MatchedTagRow) => {
      try {
        await apiService.submitTenantMatchFeedback(match.match_id, { action: 'rejected' })
        // The row leaves the table on the spot: the table already draws no line
        // for a rejected match, so marking it is removing it. The refetch below
        // then confirms against the server rather than being the first time the
        // reader sees the result.
        setMatchedGroups((prev) =>
          prev.map((group) => ({
            ...group,
            matches: group.matches.map((m) =>
              m.match_id === match.match_id ? { ...m, decision: 'rejected' as const } : m,
            ),
          })),
        )
        toast.success(
          t('data_catalog:tag_mapping.row_actions.deleted', {
            defaultValue: 'Mapping deleted successfully',
          }),
        )
        reloadAfterRowAction()
      } catch (err) {
        reportActionError(
          err,
          t('data_catalog:tag_mapping.row_actions.delete_failed', {
            defaultValue: 'Could not delete the mapping.',
          }),
        )
      }
    },
    [t, reloadAfterRowAction, reportActionError],
  )

  /**
   * Pending → Delete: rule the tag out.
   *
   * A pending row has no mapping to delete — it is a discovered tag awaiting
   * one. The thing the reader is dismissing is the TAG, so this records the
   * `not_a_tag` verdict, which suppresses the identity from the projection the
   * assistant reads. Keyed on the SPELLING (`display_tag`), which is what the
   * verdict endpoint derives the identity from.
   *
   * The server keeps a decided row in its place (see the FS-675 note above),
   * carrying its verdict; the table no longer lists a decided row, so from the
   * reader's side the row is gone. Pratima: "if we delete any mapping we
   * should remove the entry we shouldnt keep them as deleted putting a cut".
   */
  /**
   * Matched → a document-evidence line's Delete. Same verdict as Pending's
   * Delete and for the same reason: the line has no mapping to reject, so what
   * is being dismissed is the tag. Keyed on the spelling, which is what the
   * verdict endpoint derives the identity from.
   */
  const handleRuleOutTag = useCallback(
    async (tag: string) => {
      try {
        await apiService.recordTagVerdict({ tag, verdict: 'not_a_tag' })
        // The evidence line leaves on the spot; the read side now honours
        // the verdict too (`_document_mentions`), so the refetch below agrees.
        setMatchedGroups((prev) =>
          prev.map((group) => (group.tag === tag ? { ...group, documents: null } : group)),
        )
        toast.success(
          t('data_catalog:tag_mapping.row_actions.ruled_out', {
            defaultValue: 'Marked as not a tag',
          }),
        )
        reloadAfterRowAction()
      } catch (err) {
        reportActionError(
          err,
          t('data_catalog:tag_mapping.row_actions.rule_out_failed', {
            defaultValue: 'Could not record the verdict.',
          }),
        )
      }
    },
    [t, reloadAfterRowAction, reportActionError],
  )

  const handleDeletePending = useCallback(
    async (row: PendingTagRow) => {
      try {
        await apiService.recordTagVerdict({ tag: row.display_tag, verdict: 'not_a_tag' })
        // The server keeps a decided row in its place, and the table hides a
        // decided row -- so marking it here removes it from view on the spot
        // while keeping the row count the paging arithmetic is built on.
        setPendingRows((prev) =>
          prev.map((r) => (r.tag === row.tag ? { ...r, verdict: 'not_a_tag' as const } : r)),
        )
        toast.success(
          t('data_catalog:tag_mapping.row_actions.ruled_out', {
            defaultValue: 'Marked as not a tag',
          }),
        )
        reloadAfterRowAction()
      } catch (err) {
        reportActionError(
          err,
          t('data_catalog:tag_mapping.row_actions.rule_out_failed', {
            defaultValue: 'Could not record the verdict.',
          }),
        )
      }
    },
    [t, reloadAfterRowAction, reportActionError],
  )

  return (
    // UI-33 (Pegah): "Clicking Tag mapping loses the second bar on the left,
    // not consistent with the rest of the items within Data".
    //
    // This page used to omit the DataSidebar, reading the Tag mapping Figma
    // frames (Sidebar width=80) as a deliberate full-width layout. It is the
    // only page in the section that did: Data Catalog, Knowledge Graph and
    // Knowledge Capture all render it, so leaving here dropped the
    // section nav and the way back with it. The width argument does not hold
    // either — the table renders TWO columns, not the three that note assumed,
    // and the tag column is mostly empty space at this width.
    <div className="flex h-screen">
      <AppSidebar />
      <DataSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className={`${PAGE_HEADER} justify-between`}>
          <div className="flex items-center gap-2">
            {/*
              UI-32 (Putra): "Going to tag mapping from board no way back".

              Always present; only the destination depends on how you arrived.
              `?board=` is the board toolbar's scope param, so it doubles as
              the record of the journey: with it, back means that board — a
              place the section nav cannot reach. Without it the arrival came
              through Data, and the Data Catalog is this page's section home.

              The DataSidebar (restored in UI-33) offers a route back for the
              Data arrival, so the control is redundant there and Putra asked
              for it to go (2026-09-17). It survives for the BOARD arrival
              only: that is UI-32, Putra's own ticket -- "going to tag mapping
              from board no way back" -- and the section nav cannot reach a
              board. Dropping it outright would reopen the bug the button was
              added to close.

              The decorative glyph that sat beside the title is gone entirely;
              it labelled nothing the words did not already say.
            */}
            {filters.boardId && (
              <button
                type="button"
                onClick={() => navigate(backTo.path)}
                className="-ml-1.5 flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label={t(backTo.labelKey, { defaultValue: backTo.fallback })}
                title={t(backTo.labelKey, { defaultValue: backTo.fallback })}
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            <span className={PAGE_TITLE}>
              {t('data_catalog:tag_mapping.page_title', { defaultValue: 'Tag Mapping' })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <HelpAgentLauncherSlot />
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col bg-background dark:bg-dark-card">
          <div className="flex shrink-0 items-center gap-2 px-4 py-3">
            <div className="relative w-[240px]">
              <Search className="absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <IntuiInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('data_catalog:tag_mapping.search_placeholder', {
                  defaultValue: 'Search',
                })}
                className="h-9 ps-9"
              />
            </div>
            <TagMappingFilterMenu
              tab={tab}
              filters={filters}
              onChange={handleFiltersChange}
              documents={pendingDocuments}
              boards={boards}
              onOpen={loadBoards}
            />


            <IntuiTabs value={tab} onValueChange={(value) => setTab(value as TabKey)}>
              <IntuiTabsList className="h-9">
                <IntuiTabsTrigger value="matched" className="px-3">
                  {/* "Mapped", not "Matched" — every frame that draws this tab labels it
                      that way (1304:29552, 1320:194582, 1328:235270). The key keeps its
                      name so nothing downstream has to move. */}
                  {t('data_catalog:tag_mapping.tabs.matched', { defaultValue: 'Mapped' })}
                  <TabCount value={matchedCount} />
                </IntuiTabsTrigger>
                <IntuiTabsTrigger value="pending" className="px-3">
                  {t('data_catalog:tag_mapping.tabs.pending', { defaultValue: 'Pending' })}
                  {/* Approximate ONLY while the summary's estimate is what we have.
                      The tab's own total, once loaded, is exact and wins below. */}
                  <TabCount value={pendingCount} approximate={pendingApproximate} />
                </IntuiTabsTrigger>
              </IntuiTabsList>
            </IntuiTabs>
          </div>

          {/*
            Every active facet, OUTSIDE the menu (review F20 / C3). The Filter
            button's count says how many are on; this row says which, and each
            chip's × is how to stop. The board used to be the one facet drawn
            out here, because arriving from a board applies it unasked — it is
            now the first chip, with the same accessible name.
          */}
          <div className="shrink-0 px-4 pb-3 empty:hidden">
            <TagMappingFilterChips
              tab={tab}
              filters={filters}
              onChange={handleFiltersChange}
              boardName={boardName}
              documents={pendingDocuments}
            />
          </div>

          {loadUnavailable ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('data_catalog:tag_mapping.empty.counts_unavailable', {
                  defaultValue:
                    "Couldn't load your tag mappings just now. They may still be there — retry in a moment.",
                })}
              </p>
              <IntuiButton variant="outline" size="lg" onClick={handleRetryLoad}>
                {t('data_catalog:tag_mapping.empty.retry', { defaultValue: 'Retry' })}
              </IntuiButton>
            </div>
          ) : noRowsOnBoard ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('data_catalog:tag_mapping.empty.none_on_board', {
                  defaultValue: 'No {{tab}} tags on the files in {{board}}.',
                  tab: thisTabLabel.toLowerCase(),
                  board:
                    boardName ||
                    t('data_catalog:tag_mapping.filters.board', { defaultValue: 'Board' }),
                })}
              </p>
              <div className="flex items-center gap-2">
                {/*
                  The OTHER tab, still scoped to this board — the first thing
                  anyone arriving from a board wants next.

                  Offered rather than counted. Putting the number on the badge
                  would mean fetching the other tab's page on arrival, and the
                  Pending list is the tenant-wide computation `/summary`
                  deliberately refuses to run (13.2s on dev, 300s cache). A
                  button costs nothing until it is pressed, and it is what
                  turns "no mapped tags here" from a dead end into the next
                  step: the board's pending tags are a different question with
                  a different answer.
                */}
                <IntuiButton variant="outline" size="lg" onClick={() => setTab(otherTab)}>
                  {t('data_catalog:tag_mapping.empty.check_other_tab', {
                    defaultValue: 'Check {{other}} on this board',
                    other: otherTabLabel,
                  })}
                </IntuiButton>
                <IntuiButton variant="ghost" size="lg" onClick={clearBoardFilter}>
                  {t('data_catalog:tag_mapping.empty.show_all_boards', {
                    defaultValue: 'Show all tags',
                  })}
                </IntuiButton>
              </div>
            </div>
          ) : isEmptyTenant ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('data_catalog:tag_mapping.empty.run_hint', {
                  defaultValue: 'Run matching over your documents to populate this page.',
                })}
              </p>
              <IntuiButton size="lg" onClick={handleRunMatching} disabled={running}>
                {running ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    {t('data_catalog:tag_mapping.run.running', { defaultValue: 'Running…' })}
                  </>
                ) : (
                  t('data_catalog:tag_mapping.empty.run_action', {
                    defaultValue: 'Run matching',
                  })
                )}
              </IntuiButton>
            </div>
          ) : showCrossTabHint ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('data_catalog:tag_mapping.cross_tab.none_here', {
                  defaultValue: 'No {{tab}} tags match "{{query}}".',
                  tab: thisTabLabel,
                  query: debouncedSearch,
                })}
              </p>
              <IntuiButton size="lg" variant="outline" onClick={() => setTab(otherTab)}>
                {t('data_catalog:tag_mapping.cross_tab.found_other', {
                  defaultValue: 'Found {{count}} in {{other}}',
                  count: otherTabHits ?? 0,
                  other: otherTabLabel,
                })}
              </IntuiButton>
            </div>
          ) : tab === 'matched' ? (
            <MatchedTable
              groups={matchedGroups}
              sourceType={filters.sourceType}
              loading={loading}
              reloading={reloading}
              hasMore={hasMore}
              searching={!!debouncedSearch}
              onLoadMore={loadMore}
              onSelectTag={handleSelectTag}
              onSelectDocument={(id, name, mention) => handleSelectSourceFile(id, name, 'document', mention)}
              onSelectSourceFile={handleSelectSourceFile}
              onEditMapping={setEditTarget}
              onDeleteMapping={handleDeleteMatch}
              onRuleOutTag={handleRuleOutTag}
              filtered={narrowed}
              onClearFilters={clearAllFilters}
              sort={sort}
              onSort={handleSort}
            />
          ) : (
            <PendingTable
              rows={pendingRows}
              loading={loading}
              reloading={reloading}
              hasMore={hasMore}
              searching={!!debouncedSearch}
              onLoadMore={loadMore}
              onSelectTag={handleSelectTag}
              onSelectSourceFile={handleSelectSourceFile}
              onApprove={handleApprovePending}
              onEditMapping={setEditTarget}
              onDelete={handleDeletePending}
              filtered={narrowed}
              onClearFilters={clearAllFilters}
              sort={sort}
              onSort={handleSort}
            />
          )}
        </main>
      </div>

      <EditMappingDialog
        target={editTarget}
        open={!!editTarget}
        onOpenChange={(next) => {
          if (!next) setEditTarget(null)
        }}
        onMapped={() => {
          // Re-read from page 0 AND the badges: the write can move the row
          // between Matched and Pending and changes its confidence, so
          // patching it in place would show a row the server no longer agrees
          // with -- and the counts come from `/summary`, not from the list, so
          // reloading only the rows left both tab badges stale. Now that Edit
          // is offered from Pending as well, that is the common path.
          setEditTarget(null)
          reloadAfterRowAction()
        }}
      />

      {/* A Source file, over the table, in the board's own preview. */}
      {sourcePreview && (
        <SourceFilePreviewOverlay
          documentId={sourcePreview.id}
          name={sourcePreview.name}
          highlightTag={sourcePreview.highlightTag}
          onClose={() => setSourcePreview(null)}
        />
      )}

    </div>
  )
}

/** Keep shared links scoped to their original tenant before mounting any data fetches. */
const TagMapping = () => {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const organizationId = useAppStore((st) => st.activeOrganizationId)
  const organizations = useAppStore((st) => st.organizations)
  const requestedOrganization = params.get('organization')

  useEffect(() => {
    if (!requestedOrganization && organizationId) {
      setParams((previous) => {
        const next = new URLSearchParams(previous)
        next.set('organization', organizationId)
        return next
      }, { replace: true })
    }
  }, [organizationId, requestedOrganization, setParams])

  if (requestedOrganization && requestedOrganization !== organizationId) {
    const organization = organizations.find((org) => org.id === requestedOrganization)
    return (
      <div className="flex h-screen">
        <AppSidebar />
        <DataSidebar />
        <div className="p-6" role="status">
          {!organizationId
            ? t('data_catalog:tag_mapping.shared.loading', { defaultValue: 'Loading organization…' })
            : organization
              ? t('data_catalog:tag_mapping.shared.switch_organization', {
                  defaultValue: 'This link is for {{organization}}. Select that organization in your account menu, then reopen this link.',
                  organization: organization.displayName || organization.name,
                })
              : t('data_catalog:tag_mapping.shared.no_access', {
                  defaultValue: 'This link is for an organization you do not have access to.',
                })}
        </div>
      </div>
    )
  }
  return <TagMappingPage />
}

export default TagMapping
